import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { ReceevitLogoCompact } from '../common/ReceevitLogo';
import {
  LayoutDashboard,
  Users,
  FileText,
  UserCheck,
  Shield,
  Settings,
  LogOut,
  User,
  Bell,
  Menu,
  X,
  Home,
  CreditCard,
  Activity
} from 'lucide-react';

const MobileStaffLayout = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Navigation tabs for mobile staff portal
  const navigationTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/staff/dashboard' },
    { id: 'clients', label: 'Clients', icon: Users, href: '/staff/manage-clients' },
    { id: 'invoices', label: 'Invoices', icon: FileText, href: '/staff/invoices' },
    { id: 'staff', label: 'Staff', icon: UserCheck, href: '/staff/staff' },
    { id: 'security', label: 'Security', icon: Shield, href: '/staff/security' }
  ];

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/staff-login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // No longer needed - using <Outlet /> to render route components
  // This allows the ResponsiveStaffComponents to work correctly

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/staff/dashboard') setActiveTab('dashboard');
    else if (path.startsWith('/staff/manage-clients')) setActiveTab('clients');
    else if (path.startsWith('/staff/invoices')) setActiveTab('invoices');
    else if (path.startsWith('/staff/staff')) setActiveTab('staff');
    else if (path.startsWith('/staff/security')) setActiveTab('security');
    else if (path.startsWith('/staff/settings')) setActiveTab('settings');
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Mobile Header */}
      <header className="bg-[#1F11ED] text-white px-4 py-0.5 flex items-center justify-between sticky top-0 z-50">
        <Link to="/staff/dashboard" className="flex items-center">
          <ReceevitLogoCompact size={140} forBlueBackground={true} />
        </Link>
        
        <div className="flex items-center space-x-3">
          {/* Notifications */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Bell className="w-5 h-5" />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </button>

          {/* Settings */}
          <Link
            to="/staff/settings"
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Settings className="w-5 h-5" />
          </Link>

          {/* User Menu */}
          <div className="relative">
            <button 
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="absolute top-20 right-4 left-4 bg-white rounded-lg shadow-lg border z-40 max-h-80 overflow-y-auto">
          <div className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Notifications</h3>
            {notifications.length > 0 ? (
              <div className="space-y-3">
                {notifications.slice(0, 5).map((notification) => (
                  <div key={notification.id} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-900">{notification.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(notification.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No notifications</p>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-20 px-4">
        <div className="pt-4">
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-2 z-40">
        <div className="flex justify-around items-center">
          {navigationTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <Link
                key={tab.id}
                to={tab.href}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
                  isActive 
                    ? 'text-[#1F11ED] bg-blue-50' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5 mb-1" />
                <span className="text-xs font-medium truncate">{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Logout</h3>
            
            {/* User Info in Logout Modal */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#1F11ED] rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                  <p className="text-sm text-gray-600">{user?.email}</p>
                  <p className="text-xs text-gray-500">Role: {user?.role || 'admin'}</p>
                </div>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">Are you sure you want to logout from the staff portal?</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileStaffLayout;
